import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CalendarDays, Clock, CheckCircle2, XCircle, Sparkles, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const when = (iso) => { const d = new Date(iso.slice(0, 16)); return `${d.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })} at ${d.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" }).toLowerCase()}`; };
const dur = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ""}` : `${m}m`);

// Public, no-login page behind the waitlist "a spot opened" text. First to tap gets the slot.
export default function WaitlistOffer() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { axios.get(`${API}/public/offer/${token}`).then((r) => setD(r.data)).catch(() => setErr("This link isn't valid any more.")); }, [token]);

  const accept = async () => {
    setBusy(true);
    try { const r = await axios.post(`${API}/public/offer/${token}/accept`); setD(r.data); }
    catch (e) { setErr(e?.response?.data?.detail || "Something went wrong — please call the clinic."); }
    finally { setBusy(false); }
  };

  const total = d?.services?.reduce((t, s) => t + (s.duration_min || 0), 0) || 0;
  const Status = ({ icon: Icon, tone, title, body, testId }) => (
    <div className={`rounded-2xl border p-6 text-center space-y-2 ${tone}`} data-testid={testId}>
      <Icon className="h-9 w-9 mx-auto" />
      <div className="font-heading font-semibold text-lg">{title}</div>
      <p className="text-sm opacity-80">{body}</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-start justify-center p-4 sm:p-8" data-testid="offer-page">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center pt-4 font-heading font-bold text-2xl">{d?.clinic || "Azordi"}</div>
        {err && <div className="rounded-2xl border bg-card p-6 text-center text-muted-foreground" data-testid="offer-error">{err}</div>}
        {d && (
          <>
            {d.state === "offered" && (
              <div className="rounded-2xl border bg-card p-5 shadow-sm space-y-4" data-testid="offer-card">
                <div className="flex items-center gap-2 text-primary text-sm font-medium"><Sparkles className="h-4 w-4" />A spot just opened up{d.client_name ? `, ${d.client_name.split(" ")[0]}` : ""}</div>
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center shrink-0"><CalendarDays className="h-5 w-5 text-primary" /></div>
                  <div>
                    <div className="font-semibold text-lg leading-tight" data-testid="offer-when">{when(d.start)}</div>
                    <div className="text-sm text-muted-foreground mt-0.5">{d.staff ? `with ${d.staff} · ` : ""}{dur(total)}</div>
                  </div>
                </div>
                <ul className="divide-y rounded-xl border" data-testid="offer-services">
                  {d.services.map((s, i) => (
                    <li key={s.id || i} className="flex items-center justify-between px-3 py-2 text-sm"><span>{s.name}</span><span className="text-muted-foreground">{s.duration_min}m</span></li>
                  ))}
                </ul>
                <button onClick={accept} disabled={busy} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60" data-testid="offer-accept">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}{busy ? "Booking…" : "Book this spot"}
                </button>
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1"><Clock className="h-3 w-3" />First to tap gets it. Offer expires {new Date(d.expires_at).toLocaleString("en-AU", { weekday: "short", hour: "numeric", minute: "2-digit" })}.</p>
              </div>
            )}
            {d.state === "accepted" && (
              <>
                <Status icon={CheckCircle2} tone="bg-emerald-50 border-emerald-200 text-emerald-800" title="You're booked!" body={`${when(d.start)}${d.staff ? ` with ${d.staff}` : ""}. We've texted you the details.`} testId="offer-accepted" />
                {d.my_link && <a href={d.my_link} className="block text-center text-sm text-primary underline" data-testid="offer-my-link">See your appointments</a>}
              </>
            )}
            {d.state === "taken" && <Status icon={XCircle} tone="bg-card" title="Sorry, that spot's gone" body="Someone else got there first. You're still on the waitlist — we'll text you the next one." testId="offer-taken" />}
            {d.state === "expired" && <Status icon={Clock} tone="bg-card" title="This offer has expired" body="You're still on the waitlist — we'll text you when the next spot opens." testId="offer-expired" />}
            {d.slug && d.state !== "offered" && <Link to={`/book/${d.slug}`} className="block text-center text-sm text-primary underline" data-testid="offer-book-online">Book online instead</Link>}
          </>
        )}
      </div>
    </div>
  );
}
